import { Component, Input, Output, EventEmitter, OnInit, ViewChild, NgZone, OnDestroy } from '@angular/core';
import { TabsetComponent } from 'ngx-bootstrap';
import { Subscription } from 'rxjs';

import {
  BackgroundInfo,
  BackgroundInfoTopic,
  BackgroundInfoType,
  AddSubtopicModalPayload,
  CopyBackgroundInfoModalPayload,
  BackgroundInfoJurisdiction,
  Message,
  CreateBackgroundInfoTemplatePayload
} from '../../../models';

import { ModalService, ConfirmationModalComponent } from '../../../modules/modal';

import { BackgroundInfoService } from '../../../services';
import { AddSubtopicModalComponent } from '../add-subtopic-modal/add-subtopic-modal.component';
import { CopyBackgroundInfoModalComponent } from '../copy-background-info-modal/copy-background-info-modal.component';

import { TranslationConstants } from 'src/app/shared/constants/translation';
import { CreateTemplateModalComponent } from '../create-template-modal/create-template-modal.component';
import {
  ImportBackgroundInfoTemplateComponent
} from '../import-background-info-template/import-background-info-template.component';

@Component({
  selector: 'background-info-tabs',
  templateUrl: './background-info-tabs.component.html',
})
export class BackgroundInfoTabsComponent implements OnInit, OnDestroy {
  @Input() objectName: string;
  @Input() objectId: number;
  @Input() jurisdictions: BackgroundInfo[];
  @Input() activeJurisdiction: BackgroundInfo;
  @Input() activeTopic: BackgroundInfoTopic;
  @Input() useCase: BackgroundInfoType;
  @Input() preventPageChange: boolean;
  @Input() haveSubTopics: number;
  @Input() code: string;
  @Output() selectJurisdiction = new EventEmitter();
  @Output() selectTopic = new EventEmitter(true);
  @Output() preventPageChangeDueToUnSavedWork = new EventEmitter();
  @Output() getBackgroundInfo = new EventEmitter();

  @ViewChild('jurisdictionTabs') jurisdictionTabs: TabsetComponent;
  @ViewChild('topicTabs') topicTabs: TabsetComponent;

  jurisdiction: BackgroundInfoJurisdiction;
  useCaseTranslationKey: string;
  useCaseString: string;
  hideCopyForMne: boolean;
  private subscriptions: Subscription[] = [];

  constructor(
    protected modalService: ModalService,
    private _backgroundInfoService: BackgroundInfoService,
    private _ngZone: NgZone
  ) {
  }

  ngOnInit(): void {
    this.useCaseTranslationKey = this._backgroundInfoService.getTranslationKeyByBackInfoType(this.useCase);
    BackgroundInfoType[this.useCase] === 'UncontrolledTransaction' ? this.useCaseString = 'uncontrolled_transaction' : this.useCaseString = BackgroundInfoType[this.useCase].toLocaleLowerCase();
    this.hideCopyForMne = this.useCase === BackgroundInfoType.MNE;
    this.listenToJurisdictionChanged();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => { subscription.unsubscribe(); });
  }

  isTopicSelected({ topicId }: BackgroundInfoTopic): boolean {
    return this.activeTopic.topicId === topicId;
  }

  onSelectJurisdiction(jurisdiction: BackgroundInfo, skipCheck: boolean = false): void {
    if (!skipCheck && this.preventPageChange) {
      this.changeTabByIndex('jurisdiction', this.getJurisdictionTabIndex(this.activeJurisdiction));

      return this.showSubTopicEditedModal('jurisdiction', jurisdiction);
    }

    this.jurisdiction = jurisdiction.jurisdiction;
    this.selectJurisdiction.emit(jurisdiction);
  }

  onSelectTopic(topic: BackgroundInfoTopic, skipCheck: boolean = false): void {
    if (!skipCheck && this.preventPageChange) {
      this.changeTabByIndex('topic', this.getTopicTabIndex(this.activeTopic));

      return this.showSubTopicEditedModal('topic', topic);
    }

    this.selectTopic.emit(topic);
  }

  openAddNewSubtopicModal(): void {
    this.modalService.openModal({
      component: AddSubtopicModalComponent,
      data: {
        useCase: this.useCase,
        activeJurisdiction: this.activeJurisdiction,
        activeTopic: this.activeTopic,
        objectId: this.objectId,
        objectName: this.objectName,
      } as AddSubtopicModalPayload,
      success: () => {
        this.selectTopic.emit();
        this._backgroundInfoService.increaseSubTopicIndex();
      },
    });
  }

  openCreateTemplateModal(): void {
    this.modalService.openModal({
      component: CreateTemplateModalComponent,
      data: {
        useCase: this.useCase,
        activeJurisdiction: this.activeJurisdiction,
        objectId: this.objectId,
        code: this.code,
      } as CreateBackgroundInfoTemplatePayload,
      success: () => {
      },
    });
  }

  openImportTemplateModal(): void {
    this.modalService.openModal({
      component: ImportBackgroundInfoTemplateComponent,
      data: {
        useCase: this.useCase,
        activeJurisdiction: this.activeJurisdiction,
        objectId: this.objectId,
        code: this.code,
      } as CreateBackgroundInfoTemplatePayload,
      success: () => {
      },
    });
  }

  reinitialize(): void {
    this.modalService.openModal({
      component: ConfirmationModalComponent,
      data: {
        title: TranslationConstants.GLOBAL_REINITIALIZE,
        content: TranslationConstants.GLOBAL_REINITIALIZE_WARNING,
      } as Message,
      success: () => {
        const jurisdictionId = this.activeJurisdiction.jurisdiction ? this.activeJurisdiction.jurisdiction.countryId : null;
        const data = jurisdictionId ? { jurisdictionId } : {};

        this._backgroundInfoService.reinitializeBackgroundInfo(this.objectId, this.useCase, data).subscribe(() => {
          this.getBackgroundInfo.emit();
        });
      },
    });
  }

  copyBackgroundInfo(): void {
    this.modalService.openModal({
      component: CopyBackgroundInfoModalComponent,
      data: {
        destinationObjectId: this.objectId,
        destinationObjectIdentifier: this.objectName,
        useCase: this.useCase,
        jurisdiction: this.jurisdiction,
      } as CopyBackgroundInfoModalPayload,
      success: () => {
        this.selectTopic.emit();
      },
    });
  }

  getCssClassList(): string {
    let cssClassList = 'nav-item nav-link gen-tab text-center col purple-tab single';
    if (this.activeJurisdiction.topics && this.activeJurisdiction.topics.length !== 1) {
      cssClassList = 'nav-item nav-link gen-tab text-center col purple-tab';
    }
    return cssClassList;
  }

  private showSubTopicEditedModal(type: string, resource: any): void {
    this.modalService.openModal({
      component: ConfirmationModalComponent,
      data: {
        title: TranslationConstants.GLOBAL_LEAVE,
        content: TranslationConstants.GLOBAL_LEAVE_CONTENT,
      } as Message,
      success: () => {
        this.preventPageChangeDueToUnSavedWork.emit(false);
        this.preventPageChange = false;
        this.emitTabSelection(type, resource);
      },
    });
  }

  private changeTabByIndex(type: string, index: number): void {
    const tabsComponent = type === 'topic' ? this.topicTabs : this.jurisdictionTabs;
    this._ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        tabsComponent.tabs[index].active = true;
      });
    });
  }

  private listenToJurisdictionChanged(): void {
    this.subscriptions.push(
      this._backgroundInfoService.jurisdictionChanged$.subscribe((index: number) => {
        this.changeTabByIndex('jurisdiction', index);
      })
    );
  }

  private getTopicTabIndex(topic: BackgroundInfoTopic): number {
    return this.activeJurisdiction.topics.indexOf(topic);
  }

  private getJurisdictionTabIndex(jurisdiction: BackgroundInfo) {
    return this.jurisdictions.indexOf(jurisdiction);
  }

  private emitTabSelection(type: string, resource: any): void {
    this.onSelectTopic(resource, true);
    if (type === 'topic') {
      this.changeTabByIndex(type, this.getTopicTabIndex(resource));
    } else {
      this.changeTabByIndex(type, this.getJurisdictionTabIndex(resource));
    }
  }
}
